import type { InstructorAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorAssignmentCubit'
import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useDialogs } from 'hooks/dialogs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateTime } from 'luxon'
import { AssignmentState } from '@breakoutlearning/firebase-repository/types'
import { noTryAsync } from '@breakoutlearning/firebase-repository/util'
import { toast } from 'react-hot-toast'
import { useTranslationTyped } from 'i18n/i18n'
import { LtiBaseAssignmentDialog } from './LtiBaseAssignmentDialog'
import { getAssignmentSchema } from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import type { AssignmentFormValues } from 'pages/instructor/slide_deck/AssignmentDialog/schemas'
import { deleteField } from 'firebase/firestore'
import { useLTIMode } from 'hooks/use-lti-mode'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'
import { ResyncGradesDialog } from 'pages/instructor/dialogs/ResyncGradesDialog'
import { SpinnerCentered } from 'components/Spinner'
import { defaultPeerReviewCriteria } from 'pages/instructor/slide_deck/AssignmentDialog/helpers'
import { useFunctions } from 'hooks/auth'
import { useFeatureFlags } from 'hooks/featureFlags'
import { ConfigureCustomRubrics } from 'pages/instructor/assignment/dialogs/ConfigureCustomRubrics'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import {
  isRubricEligibleForCustomGrading,
  type CustomRubricsConfig,
} from 'pages/instructor/assignment/dialogs/customRubricsConfig'
import { saveCustomRubrics } from 'pages/instructor/slide_deck/AssignmentDialog/saveCustomRubrics'

export const LtiEditAssignmentDialog = observer(
  function LtiEditAssignmentDialog({
    assignment,
    cubit,
  }: {
    assignment: SectionAssignment
    cubit: InstructorAssignmentCubit
  }) {
    const peerReviewsEnabled = cubit.peerReviewController.peerReviewsEnabled

    if (!peerReviewsEnabled.isLoaded) {
      return <SpinnerCentered />
    }

    return (
      <LtiEditAssignmentDialogForm
        assignment={assignment}
        cubit={cubit}
        peerReviewsEnabled={peerReviewsEnabled.value}
      />
    )
  }
)

const LtiEditAssignmentDialogForm = observer(
  function LtiEditAssignmentDialogForm({
    assignment,
    cubit,
    peerReviewsEnabled,
  }: {
    assignment: SectionAssignment
    cubit: InstructorAssignmentCubit
    peerReviewsEnabled: boolean
  }) {
    const { tt } = useTranslationTyped()
    const { popDialog, showDialog } = useDialogs()
    const scalarsRef =
      useRef<SectionAssignment['data']['assignmentGradingScalars']>(undefined)
    const { ltiModeOn } = useLTIMode()
    const functions = useFunctions()
    const { customRubricsEnabled, useApiForCustomRubrics } = useFeatureFlags()
    const canConfigureCustomRubrics =
      customRubricsEnabled &&
      cubit.slideRubrics.models.some(isRubricEligibleForCustomGrading)
    const customRubricsRef = useRef<CustomRubricsConfig>({
      enabled: false,
      isValid: true,
      rubricConfigs: [],
    })
    const [customRubricsValid, setCustomRubricsValid] = useState(true)
    const [weightsDialogOpen, setWeightsDialogOpen] = useState(false)

    const hasBeenStarted = cubit.hasBeenStarted
    const releasedToStudents =
      cubit.section.studentsIncludingRemovedCount > 0 &&
      assignment.data.assignmentState > 0
    const hasQuestions = cubit.questionsCollection.models.some(
      (q) => q.isGraded
    )
    const hasRubrics = cubit.slideRubrics.models.length > 0
    const isDraft = assignment.data.assignmentState <= AssignmentState.draft

    const schema = useMemo(() => {
      const dueDate = assignment.dueDate

      return getAssignmentSchema({
        tt,
        extendMinExpiresAtTo: dueDate // This is non-obvious, but due to the relation between DB fields and form fields, we want to use the earliest of the DB fields
          ? {
              date: DateTime.fromJSDate(dueDate),
              message: tt.instructor_assignment.can_only_be_moved_forward(),
            }
          : undefined,
        sectionEndDate: cubit.section.data.sectionEndDate,
        sectionStartDate: cubit.section.data.sectionStartDate,
      })
    }, [
      assignment.dueDate,
      tt,
      cubit.section.data.sectionEndDate,
      cubit.section.data.sectionStartDate,
    ])

    const formPeerReviewCriteria = useMemo(() => {
      const existingCriteria =
        cubit.peerReviewController.peerReviewQuestions.models
          .slice()
          .sort((a, b) => (a.data.prOrder ?? 0) - (b.data.prOrder ?? 0))
          .map((q) => ({
            prQuestion: q.data.prQuestion || '',
            prDescription: q.data.prDescription || '',
          }))

      if (existingCriteria.length === 0) {
        return defaultPeerReviewCriteria(tt)
      }

      return existingCriteria
    }, [cubit.peerReviewController.peerReviewQuestions.models, tt])

    const form = useForm<AssignmentFormValues>({
      resolver: zodResolver(schema),
      mode: 'onChange',
      defaultValues: {
        catalogId: assignment.data.catalogId ?? undefined,
        customGroupingSelected: assignment.data.customGroupingSelected,
        assignedAt: assignment.assignedAt
          ? DateTime.fromJSDate(assignment.assignedAt)
          : undefined,
        dueDate: assignment.dueDate
          ? DateTime.fromJSDate(assignment.dueDate)
          : undefined,
        allowLateSubmissions: !!assignment.assignmentOverdueAt,
        lateSubmissionCutoff:
          assignment.assignmentOverdueAt && assignment.expiresAt
            ? DateTime.fromJSDate(assignment.expiresAt)
            : undefined,
        assignmentType: assignment.assignmentType,
        groupingType: assignment.groupingType,
        sectionId: assignment.sectionId,
        groupingSize: assignment.maxGroupSize,
        groupingSizeMinimum: assignment.data.groupingSizeMinimum,
        groupingSizeMaximum: assignment.maxGroupSize,
        configureGrading:
          assignment.data.assignmentGradingScalars !== undefined,
        forbidStudentGroupCreation: assignment.data.forbidStudentGroupCreation,
        forbidStudentGroupScheduling:
          assignment.data.forbidStudentGroupScheduling,
        forbidStudentGroupSelection:
          assignment.data.forbidStudentGroupSelection,
        forbidStudentGroupJoining: assignment.forbidStudentGroupJoining,
        forbidStudentGroupLeaving: assignment.forbidStudentGroupLeaving,
        peerReviewsEnabled,
        peerReviewCriteria: formPeerReviewCriteria,
        peerReviewExcludesSelfReview:
          assignment.data.peerReviewExcludesSelfReview,
        peerReviewsHiddenFromReviewees:
          assignment.data.peerReviewsHiddenFromReviewees,
        showFinalGradeToParticipants:
          assignment.data.showFinalGradeToParticipants ?? false,
      },
    })

    const onSubmit = useCallback(
      async (data: Partial<AssignmentFormValues>) => {
        const assignmentGradingScalars = data.configureGrading
          ? scalarsRef.current
          : undefined

        const finalPayload = {
          ...data,
          assignmentGradingScalars: assignmentGradingScalars ?? deleteField(),
          showFinalGradeToParticipants: data.showFinalGradeToParticipants,
        }

        // we don't want these to be sent to the server
        delete finalPayload.catalogId
        delete finalPayload.configureGrading

        const customRubrics = customRubricsRef.current

        // Gate custom-rubric handling on the live grading toggle: the panel's
        // ref stays enabled after it unmounts, so switching grading to "No"
        // must neither block the save nor persist rubrics for an ungraded
        // assignment.
        const customRubricsActive =
          !!data.configureGrading && customRubrics.enabled

        const [, err] = await noTryAsync(async () => {
          if (customRubricsActive && !customRubrics.isValid) {
            throw new Error('invalid custom rubric configuration')
          }
          await cubit.updateAssignment(finalPayload)

          await cubit.savePeerReviewConfigFromFormFields({
            peerReviewsEnabled: form.getValues('peerReviewsEnabled') ?? false,
            peerReviewCriteria: data.peerReviewCriteria ?? [],
            peerReviewExcludesSelfReview: data.peerReviewExcludesSelfReview,
            peerReviewsHiddenFromReviewees: data.peerReviewsHiddenFromReviewees,
          })

          if (customRubricsActive) {
            await saveCustomRubrics({
              useApi: useApiForCustomRubrics,
              functions,
              sectionId: cubit.sectionId,
              assignmentId: cubit.assignmentId,
              rubricConfigs: customRubrics.rubricConfigs,
            })
          }
        })
        if (err) {
          toast.error(tt.instructor_assignment.save_failed())
          throw err
        }

        popDialog()

        // if we are changing grading criteria on an LTI assignment with completed rooms
        // prompt the professor to resync the grades
        if (
          cubit.section.isLtiCourse &&
          cubit.roomStatesStatuses.some((s) => s === RoomStateStatus.completed)
        ) {
          showDialog(() => (
            <ResyncGradesDialog
              sectionId={cubit.sectionId}
              assignmentId={cubit.assignmentId}
            />
          ))
        }
      },
      [
        cubit,
        form,
        functions,
        popDialog,
        showDialog,
        tt.instructor_assignment,
        useApiForCustomRubrics,
      ]
    )

    // The weights dialog lives inside ConfigureCustomRubrics, so the "Customize
    // weights" link only works when that panel actually mounts — not in the
    // load-failed or still-loading states.
    const rubricsErrored =
      !!assignment.data.usesCustomRubrics && cubit.assignmentRubricsLoadFailed
    const rubricsLoading =
      !!assignment.data.usesCustomRubrics && !cubit.assignmentRubrics.isLoaded
    const rubricsPanelInteractive =
      canConfigureCustomRubrics && !rubricsErrored && !rubricsLoading

    return (
      <LtiBaseAssignmentDialog
        assignment={assignment}
        form={form}
        onSubmit={onSubmit}
        releasedToStudents={releasedToStudents}
        hasBeenStarted={hasBeenStarted}
        hasQuestions={hasQuestions}
        hasRubrics={hasRubrics}
        submitButtonText={tt.instructor_class.save_text()}
        ltiMode={ltiModeOn}
        fullScreen={ltiModeOn}
        scalarsRef={scalarsRef}
        initialAssignmentGradingScalars={
          assignment.data.assignmentGradingScalars
        }
        disableGroupingReason={
          releasedToStudents && assignment.isExpired
            ? tt.instructor_assignment.groups.disabled_cannot_edit_grouping_expired_assignment()
            : undefined
        }
        isDraft={isDraft}
        submitButtonIncludeChevron={true}
        currentScalarsToCheckDirty={
          assignment.data.assignmentGradingScalars ?? null
        }
        slideDeckId={cubit.slideDeck.id}
        customRubricsValid={customRubricsValid}
        onCustomizeWeights={
          rubricsPanelInteractive ? () => setWeightsDialogOpen(true) : undefined
        }
        advancedGradingSlot={
          canConfigureCustomRubrics ? (
            rubricsErrored ? (
              // The assignment_rubric read failed (e.g. permission-denied). Show
              // an explicit error rather than seeding the panel with scale
              // defaults (a save would clobber the saved config). The panel never
              // mounts, so configRef stays disabled and Save leaves it untouched.
              <div className="flex flex-col items-center justify-center gap-2 p-4 text-center">
                <TriangleAlertIcon size={24} className="stroke-core-error" />
                <div className="text-body-medium text-on-surface-var">
                  {tt.instructor_library.custom_rubrics_load_failed()}
                </div>
              </div>
            ) : rubricsLoading ? (
              // wait for the assignment_rubric stream so the panel seeds its
              // defaults from the saved config rather than scale defaults
              <SpinnerCentered />
            ) : (
              <ConfigureCustomRubrics
                rubrics={cubit.slideRubricsSorted}
                roles={cubit.roles.models}
                existingConfig={cubit.customRubricsById}
                configRef={customRubricsRef}
                onValidityChange={setCustomRubricsValid}
                weightsDialogOpen={weightsDialogOpen}
                onWeightsDialogClose={() => setWeightsDialogOpen(false)}
                defaultsManageableInSettings={false}
              />
            )
          ) : undefined
        }
      />
    )
  }
)
